/** TermsReacceptModal — D4 W1 Gap 13 full-screen forced re-acceptance. */
import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Space, Alert, Divider } from 'antd';
import { Sparkles, FileText, ShieldCheck, ExternalLink, RefreshCcw } from 'lucide-react';
import api from '../utils/api';
const { Title, Text, Paragraph } = Typography;
interface PendingTerm {
  document_type: 'tos' | 'privacy' | 'dpa';
  version: string;
}
interface CurrentRow {
  version: string;
  document_type: string;
  content_url: string;
  summary?: string | null;
  effective_from: string;
}
interface CurrentResp {
  tos: CurrentRow | null;
  privacy: CurrentRow | null;
  dpa: CurrentRow | null;
}
const LABELS: Record<string, { label: string; icon: React.ReactNode; tone: string }> = {
  tos: { label: 'Termos de Uso', icon: <FileText size={18} />, tone: '#6366F1' },
  privacy: { label: 'Política de Privacidade', icon: <ShieldCheck size={18} />, tone: '#10B981' },
  dpa: { label: 'DPA — Tratamento de Dados', icon: <FileText size={18} />, tone: '#8B5CF6' },
};

const SkeletonItem: React.FC = () => (
  <div
    aria-hidden
    style={{
      height: 64,
      borderRadius: 12,
      background:
        'linear-gradient(90deg, #F8FAFC 0%, #F1F5F9 50%, #F8FAFC 100%)',
      backgroundSize: '200% 100%',
      animation: 'janus-terms-shimmer 1.4s infinite linear',
      border: '1px solid #E2E8F0',
    }}
  />
);

export const TermsReacceptModal: React.FC<{
  open: boolean;
  pending: PendingTerm[];
  onAccepted: () => void;
}> = ({ open, pending, onAccepted }) => {
  const [current, setCurrent] = useState<CurrentResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    setError(null);
    api
      .get<CurrentResp>('/terms/current')
      .then((r) => setCurrent(r.data))
      .catch(() =>
        setError(
          'Não conseguimos carregar a versão atual dos termos. Verifique sua conexão e tente novamente.',
        ),
      )
      .finally(() => setLoading(false));
  }, [open, retryNonce]);
  const accept = async () => {
    setSubmitting(true);
    setError(null);
    try {
      for (const item of pending) await api.post(`/terms/${item.document_type}/accept`, { version: item.version });
      onAccepted();
    } catch {
      setError('Falha ao registrar o aceite. Já avisamos a engenharia — tente novamente em instantes.');
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <Modal
      open={open}
      footer={null}
      closable={false}
      maskClosable={false}
      keyboard={false}
      width={620}
      centered
      destroyOnClose
      styles={{ content: { padding: 0, overflow: 'hidden', borderRadius: 16 }, body: { padding: 0 } }}
    >
      {/* Top accent stripe — denotes a system-mandated step */}
      <div
        aria-hidden
        style={{ height: 6, background: 'linear-gradient(90deg, #6366F1 0%, #8B5CF6 50%, #EC4899 100%)' }}
      />
      <div style={{ padding: '32px 32px 24px', position: 'relative', overflow: 'hidden' }}>
        <div
          aria-hidden
          style={{
            position: 'absolute', right: -60, top: -60,
            width: 200, height: 200, borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.10), transparent 70%)',
            filter: 'blur(8px)', pointerEvents: 'none',
          }}
        />
        <Space align="center" style={{ marginBottom: 12, position: 'relative' }}>
          <div
            style={{
              width: 38,
              height: 38,
              borderRadius: 10,
              background: 'linear-gradient(135deg, #EEF2FF 0%, #F5F3FF 100%)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#6366F1',
              boxShadow: 'inset 0 0 0 1px rgba(99,102,241,0.16)',
            }}
          >
            <Sparkles size={18} />
          </div>
          <Title level={4} style={{ margin: 0, fontWeight: 600 }}>
            Atualizamos os termos da plataforma
          </Title>
        </Space>
        <Paragraph type="secondary" style={{ fontSize: 14, lineHeight: 1.6, position: 'relative' }}>
          Mudanças pequenas, mas importantes — o suficiente pra pedir um sim novo. Você consulta e aceita
          aqui mesmo, sem desviar do trabalho.
        </Paragraph>
        {loading ? (
          <Space direction="vertical" size={12} style={{ width: '100%' }}>
            <SkeletonItem />
            <SkeletonItem />
          </Space>
        ) : (
          <Space direction="vertical" size={12} style={{ width: '100%', marginTop: 8 }}>
            {pending.map((p) => {
              const meta = LABELS[p.document_type] ?? { label: p.document_type, icon: <FileText size={18} />, tone: '#6366F1' };
              const cur = current?.[p.document_type as keyof CurrentResp] ?? null;
              return (
                <div
                  key={p.document_type}
                  style={{
                    border: '1px solid #E2E8F0',
                    borderRadius: 12,
                    padding: 16,
                    background: 'linear-gradient(135deg, #FAFAFE 0%, #FFFFFF 100%)',
                    transition: 'border-color 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = `${meta.tone}55`;
                    e.currentTarget.style.boxShadow = `0 4px 12px ${meta.tone}14`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = '#E2E8F0';
                    e.currentTarget.style.boxShadow = '';
                  }}
                >
                  <Space style={{ width: '100%', justifyContent: 'space-between' }} align="start">
                    <Space align="start">
                      <span
                        style={{
                          color: meta.tone,
                          width: 32, height: 32, borderRadius: 8,
                          background: `${meta.tone}14`,
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        }}
                      >
                        {meta.icon}
                      </span>
                      <div>
                        <Text strong style={{ display: 'block' }}>
                          {meta.label}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Versão{' '}
                          <span style={{ fontFamily: '"JetBrains Mono", "SF Mono", Menlo, monospace' }}>
                            {p.version}
                          </span>
                          {cur?.summary ? ` · ${cur.summary}` : ''}
                        </Text>
                      </div>
                    </Space>
                    {cur?.content_url && (
                      <Button
                        type="link"
                        href={cur.content_url}
                        target="_blank"
                        rel="noreferrer noopener"
                        icon={<ExternalLink size={14} />}
                        style={{ padding: 0 }}
                      >
                        Ler versão completa
                      </Button>
                    )}
                  </Space>
                </div>
              );
            })}
          </Space>
        )}
        <div role="alert" aria-live="polite">
          {error && (
            <Alert
              type="error"
              showIcon
              message={error}
              action={
                <Button
                  size="small"
                  icon={<RefreshCcw size={12} />}
                  onClick={() => setRetryNonce((n) => n + 1)}
                >
                  Tentar de novo
                </Button>
              }
              style={{ marginTop: 16, borderRadius: 10 }}
            />
          )}
        </div>
        <Divider style={{ margin: '24px 0 16px' }} />
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>
            Aceite registrado com timestamp e IP, conforme LGPD art. 8º.
          </Text>
          <Button
            type="primary"
            size="large"
            onClick={accept}
            loading={submitting}
            disabled={loading || pending.length === 0 || !!error}
            aria-busy={submitting}
            style={{
              borderRadius: 10,
              background: 'linear-gradient(135deg, #6366F1 0%, #8B5CF6 100%)',
              border: 'none',
              boxShadow: '0 8px 24px rgba(99,102,241,0.32)',
              fontWeight: 600,
              minWidth: 140,
              transition: 'transform 200ms cubic-bezier(0.22,1,0.36,1), box-shadow 200ms ease',
            }}
            onMouseEnter={(e) => {
              const el = e.currentTarget as HTMLButtonElement;
              if (!el.disabled) el.style.transform = 'translateY(-1px)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.transform = 'translateY(0)';
            }}
          >
            Aceitar e continuar
          </Button>
        </div>
      </div>
    </Modal>
  );
};
export default TermsReacceptModal;

// Self-contained shimmer keyframe.
if (typeof document !== 'undefined' && !document.getElementById('janus-terms-keyframes')) {
  const style = document.createElement('style');
  style.id = 'janus-terms-keyframes';
  style.innerHTML = `@keyframes janus-terms-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }`;
  document.head.appendChild(style);
}
